import React from 'react';
import { Table } from '@plerionhq/design-system/experimental';
import clsx from 'clsx';
import type { GenericItem, PaginatedData } from './types';
import { safeJSONParse } from './utils';
import type {
  TableVisualizationConfig,
  VisualizationMeta,
} from '../../visualization-schema';
import { useTableColumns } from './hooks/useTableColumns';
import { useFormattedData } from './hooks/useFormattedData';
import { TableHeader } from './components/TableHeader';
import { PleriActionCell, type MoreOption } from './components/PleriActionCell';
import { ToolUseBlock, ToolResultBlock } from '../../../../store/chatStore';

import { useTranslation } from 'react-i18next';

export interface GenericTableRendererProps {
  result: ToolResultBlock;
  toolUse: ToolUseBlock;
  moreOptionsOverride?: MoreOption[];
}

export const GenericTableRenderer = ({
  result,
  toolUse,
  moreOptionsOverride,
}: GenericTableRendererProps) => {
  const { t } = useTranslation('pleri/chat');
  const parsedContent = safeJSONParse(result.content);
  const content = parsedContent as PaginatedData<GenericItem>;
  const visualizationMeta = (
    toolUse.meta as { visualization?: VisualizationMeta }
  )?.visualization;
  const visualizationConfig =
    visualizationMeta?.primaryVisualization as TableVisualizationConfig;

  const columns = useTableColumns({
    content,
    visualizationConfig,
  });

  const formattedData = useFormattedData({
    data: content?.data || [],
  });

  if (!content?.data || !Array.isArray(content.data)) {
    return (
      <div className="p-4 text-gray-600 dark:text-gray-400">
        {t('noDataAvailableOrInvalidDataStructure')}
      </div>
    );
  }

  return (
    <div className="px-4 pb-10">
      <div
        className={clsx(visualizationConfig?.containerClassName)}
        style={visualizationConfig?.containerStyle}
      >
        <TableHeader
          visualizationMeta={visualizationMeta}
          totalResults={content.data.length}
        />

        <div className="relative">
          <Table
            data={formattedData}
            columns={[
              ...(columns || []),
              {
                id: 'actions',
                header: '',
                cell: ({ row }: { row: { original: GenericItem } }) => {
                  return (
                    <PleriActionCell
                      row={row}
                      showRemediationButton={
                        visualizationConfig?.showRemediationButton ?? false
                      }
                      moreOptions={moreOptionsOverride}
                    />
                  );
                },
              },
            ]}
            sorting={{
              enabled: true,
              defaultSortBy: visualizationConfig?.defaultSort
                ? [
                    {
                      id: visualizationConfig.defaultSort.field,
                      desc:
                        visualizationConfig.defaultSort.direction === 'desc',
                    },
                  ]
                : undefined,
            }}
            row={{
              className:
                'group relative overflow-visible hover:bg-gray-50 dark:hover:bg-gray-800',
            }}
          />
        </div>
      </div>
    </div>
  );
};
